import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ApplicationStateService } from '../../services/application-state.service';
import { CommunicationService } from '../../services/communication.service';
import { BookConfigService } from '../../services/book-config.service';
import { PageService } from '../../services/page.service';
import { BookFeatureService } from '../../services/book-feature.service';
import { PageChangedEvent } from '../../utils/events/page-changed-event';
import { KeyValues } from '../../utils/key-values';
import { Point, ZoomService } from '../../services/zoom.service';
import { ChapterService } from '../../services/chapter.service';
import { UserDataService } from '../../services/user-data.service';
import { PageEditMode } from '../../utils/page-edit-modes/page-edit-mode';
import { HelpService } from '../../services/help.service';
import { QuickLinkService } from '../../services/quick-link.service';
import { environment } from '../../../environments/environment';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BookFeatureActivationService } from '../../services/book-feature-activation.service';
import { PageFavoriteService } from '../../services/page-favorite.service';
import { PositioningService } from '../../services/positioning.service';
import { FabricService } from '../../services/fabric.service';
import { SearchService } from '../../services/search.service';
import { SidebarService } from '../../services/sidebar.service';
import { BookService } from '../../services/book.service';
import { SchoolBagFileService } from '../../services/school-bag-file.service';
import { RectPageEditMode } from '../../utils/page-edit-modes/rect-page-edit-mode';
import { SelectionPageEditMode } from '../../utils/page-edit-modes/selection-page-edit-mode';
import { BookAudioService } from '../../services/book-audio.service';
import { LinkService } from '../../services/link.service';
import { RectService } from '../../services/rect.service';
import { BookVideoService } from '../../services/book-video.service';
import { PageTextService } from '../../services/page-text.service';
import axios from 'axios';
import { sidebarConfig } from '../../../environments/configs/sidebar-config';
import { HomeworkService } from '../../services/homework.service';
import _ from 'lodash';
import { QuickLink } from '../../models/quick-link';
import { CopyingPageEditMode } from '../../utils/page-edit-modes/copying-page-edit-mode';
import { BookviewerWhiteboardDrawingsService } from '../../services/bookviewer-whiteboard-drawings.service';
import { SchoolClassService } from '../../services/school-class.service';
import { CrudService } from '../../services/crud.service';
import { TechnicalFeature } from '@lemberger/lemberger-am';
import { TooltipUtils } from '../../utils/tooltip-utils';
import { CopyHudComponent } from '../footer/mobile-tools-bar/copy-hud/copy-hud.component';
import { PagesComponent } from './pages/pages.component';
import { CopyTextHudComponent } from '../footer/mobile-tools-bar/copy-text-hud/copy-text-hud.component';
import { BuyBookComponent } from '../buy-book/buy-book.component';
import { ToolsBarComponent } from '../tools-bar/tools-bar.component';
import { TopbarComponent } from '../topbar/topbar/topbar.component';
import { SidebarComponent } from '../sidebar/sidebar/sidebar.component';
import {
    InitializationLoadingIndicatorComponent
} from '../utils/initialization-loading-indicator/initialization-loading-indicator.component';
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';
import { AccessibilityService } from '../../services/accessibility.service';

@Component({
    selector: 'app-bookviewer',
    templateUrl: './bookviewer.component.html',
    styleUrls: ['./bookviewer.component.scss'],
    standalone: true,
    imports: [
        CopyHudComponent,
        PagesComponent,
        CopyTextHudComponent,
        BuyBookComponent,
        ToolsBarComponent,
        TopbarComponent,
        SidebarComponent,
        InitializationLoadingIndicatorComponent,
        SafeHtmlPipe
    ]
})
export class BookviewerComponent implements OnInit, OnDestroy {

    isInitialized = false;
    initFailed = false;
    sidebarConfig = sidebarConfig;

    /**
     * Workaround variable start
     */
    edividualSrc: string;
    showEdividualIFrame: boolean;
    /*
    Workaround variable end
     */

    pageEditMode: PageEditMode;
    rectPageEditModeClassName = RectPageEditMode.getClassName();
    selectionPageEditModeClassName = SelectionPageEditMode.getClassName();
    copyPageEditModeClassName = CopyingPageEditMode.getClassName();

    private sidebarSubscription: Subscription;
    private lastPinchDistance: number;

    constructor(public applicationStateService: ApplicationStateService,
                public helpService: HelpService,
                private communicationService: CommunicationService,
                private bookConfigService: BookConfigService,
                private bookFeatureService: BookFeatureService,
                private featureActivationService: BookFeatureActivationService,
                private userDataService: UserDataService,
                private deviceService: DeviceDetectorService,
                private zoomService: ZoomService,
                private pageService: PageService,
                private pageFavoriteService: PageFavoriteService,
                private quickLinkService: QuickLinkService,
                private positioningService: PositioningService,
                private fabricService: FabricService,
                private linkService: LinkService,
                private chapterService: ChapterService,
                private rectService: RectService,
                private audioService: BookAudioService,
                private videoService: BookVideoService,
                private pageTextService: PageTextService,
                private searchService: SearchService,
                private sidebarService: SidebarService,
                private bookService: BookService,
                private schoolBagService: SchoolBagFileService,
                private homeworkService: HomeworkService,
                // @ts-ignore
                private bookviewerWhiteboardService: BookviewerWhiteboardDrawingsService,
                private schoolClassService: SchoolClassService,
                protected accessibilityService: AccessibilityService,
                private crudService: CrudService) {
    }

    /**
     * Called after component creation.
     */
    ngOnInit(): void {
        this.init();
        axios.defaults.xsrfCookieName = null;
        axios.defaults.xsrfHeaderName = null;
        TooltipUtils.initToolTips(null, true);
    }

    /**
     * Called before component deletion.
     */
    ngOnDestroy(): void {
        this.sidebarSubscription.unsubscribe();
    }

    /**
     * Handler for key-down event.
     * - Handles arrows for page changing
     * - Handles plus/minus keys for zoom
     * - Handles cmd/ctrl+f for search
     * - Handles escape for closing sidebar
     *
     * @param $event
     */
    onKeyDown($event): void {
        if (this.anyModalOpen() || this.helpService.isHelpOpened()) {
            // Prevent action if any modal oder help is open
            return;
        }

        switch (KeyValues.getKey($event)) {
            case 'Left':
            case 'ArrowLeft': {
                if (this.isPageTurningForbidden($event)) {
                    return;
                }
                this.communicationService.announcePageTurn(PageChangedEvent.createTurnPageBackwardsEvent());
                break;
            }
            case 'Right':
            case 'ArrowRight': {
                if (this.isPageTurningForbidden($event)) {
                    return;
                }
                this.communicationService.announcePageTurn(PageChangedEvent.createTurnPageForwardEvent());
                break;
            }
            case '+': {
                this.zoomService.zoomOneStepIn();
                break;
            }
            case '-': {
                this.zoomService.zoomOneStepOut();
                break;
            }
            case 'f':
            case 'F': {
                if (!this.applicationStateService.userData.loggedIn) {
                    break;
                }

                if ($event.ctrlKey || $event.metaKey) {
                    this.searchService.startSearch();
                    $event.preventDefault();
                }
                break;
            }
            case 'Esc':
            case 'Escape': {
                this.homeworkService.checkAlert().then(checked => {
                    if (checked) {
                        this.sidebarService.closeSidebar();
                        $event.preventDefault();
                    }
                });

                break;
            }
            default:
                break;
        }
    }

    onMouseUp(): void {
        this.applicationStateService.preventPanning = false;
    }

    /**
     * Handler for pinch-in event.
     * - Handle pinch-to-zoom on mobile devices
     *
     * @param $event
     */
    onPinchIn($event): void {
        if (this.anyModalOpen()) {
            return;
        }

        this.closeOpenElements();
        $event.preventDefault();
        let eventDistance = this.calculateDistance($event);
        eventDistance = Math.max(eventDistance, (environment.maxZoomLevel - environment.minZoomLevel) / environment.amountZoomLevels);

        if (!this.lastPinchDistance) {
            this.lastPinchDistance = eventDistance;
        }

        this.zoomService.zoomStepsOut(eventDistance / this.lastPinchDistance * this.applicationStateService.zoomLevel, $event.center);
        this.lastPinchDistance = eventDistance;
    }

    /**
     * Handler for pinch-out event.
     * - Handle pinch-to-zoom on mobile devices
     *
     * @param $event
     */
    onPinchOut($event): void {
        if (this.anyModalOpen()) {
            return;
        }

        this.closeOpenElements();
        $event.preventDefault();
        let eventDistance = this.calculateDistance($event);
        eventDistance = Math.max(eventDistance, (environment.maxZoomLevel - environment.minZoomLevel) / environment.amountZoomLevels);

        if (!this.lastPinchDistance) {
            this.lastPinchDistance = eventDistance;
        }

        this.zoomService.zoomStepsIn(eventDistance / this.lastPinchDistance * this.applicationStateService.zoomLevel, $event.center);
        this.lastPinchDistance = eventDistance;

    }

    /**
     * Handler for scroll event.
     * - Handle zooming with mouse wheel
     *
     * @param $event
     */
    onScroll($event): void {
        if (this.anyModalOpen() ||
            this.helpService.isHelpOpened() ||
            this.applicationStateService.isMobileDropdownActive) {
            return;
        }

        this.closeOpenElements();
        $event.stopPropagation();

        if (this.applicationStateService.currentViewMode.slug.includes('scroll')) {
            return;
        }

        const mousePoint: Point = new Point({
            x: $event.clientX,
            y: $event.clientY
        });

        let wheelDelta = 0;
        const browser = this.deviceService.getDeviceInfo().browser;

        if ($event.wheelDelta) {
            wheelDelta = $event.wheelDelta; // All other
        } else if (browser === 'firefox') {
            wheelDelta = -($event.deltaY / Math.abs($event.deltaY)) * 100; // Firefox
        } else if ($event.deltaY) {
            wheelDelta = -parseInt($event.deltaY, 10); // IE 8/9/10
        }

        wheelDelta = wheelDelta / 120;
        const zoomSteps: number = Math.abs(wheelDelta);

        if (wheelDelta > 0) {
            this.zoomService.zoomStepsIn(zoomSteps, mousePoint);
        } else if (wheelDelta < 0) {
            this.zoomService.zoomStepsOut(zoomSteps, mousePoint);
        }

    }

    /**
     * Returns true if page turning is not allowed. e.g. if Modal is open
     * @param $event
     * @returns {boolean}
     */
    isPageTurningForbidden($event): boolean {

        if ($('.modal-backdrop.fade.in').length > 0) {
            return true;
        }

        if ($event.srcElement) {
            if ($($event.srcElement)
                .is('input:not([type=\'range\'])')) {
                return true;
            }
        }

        return false;
    }

    /**
     * Check if any modal is opened.
     *
     * @returns {boolean}
     */
    anyModalOpen(): boolean {
        return ($('.modal.show').length > 0);
    }

    getPageEditModeName(): string {

        if (this.applicationStateService.pageEditMode) {
            return this.applicationStateService.pageEditMode.constructor.name;
        }

        return '';
    }

    /**
     * Initialize the book viewer.
     */
    private init(): void {
        // 0. Initialize application state (mandatory)
        this.applicationStateService.init();

        // 1. Load book config (mandatory)
        this.bookConfigService.init(this.applicationStateService.bookId)
            .then(() => {
                // 2. Load user data (mandatory)
                this.userDataService.init()
                    .then(() => {
                        this.featureActivationService.init();

                        // 3. Load pages (mandatory)
                        this.pageService.init()
                            .then(() => {

                                let requests;
                                if (this.applicationStateService.isOfflineModeEnabled) {
                                    requests = [
                                        this.linkService.getLinks(),
                                        this.chapterService.getChapters(),
                                        this.videoService.getVideos(),
                                        this.audioService.getAudios(),
                                        this.rectService.getRects(),
                                        this.pageFavoriteService.getPageFavorites(),
                                        this.pageTextService.getTextSelections(),
                                        this.fabricService.getPageDrawings(),
                                        this.bookService.getUsedBooks(),
                                        this.quickLinkService.fetchQuickLinks(),
                                        this.schoolBagService.getSchoolBagFiles()
                                    ];
                                } else {
                                    requests = [
                                        this.bookFeatureService.getAllBookFeaturesOfAllPages(),
                                        this.bookService.getUsedBooks(),
                                        this.quickLinkService.fetchQuickLinks(),
                                        this.schoolBagService.getSchoolBagFiles(),
                                        this.homeworkService.getHomeworkLinks(false),
                                        (
                                            (this.applicationStateService.bookConfig.bookPermissions.whiteboard.view &&
                                                this.applicationStateService.bookConfig.whiteboard) ?
                                                this.bookviewerWhiteboardService.getWhiteboards(
                                                    this.applicationStateService.bookId
                                                ).toPromise()
                                                : null)
                                    ];
                                }

                                // 4. Load
                                Promise.all(requests)
                                    .then(() => {
                                        this.initFinished();
                                    });
                            })
                            .catch(err => {
                                console.error('An error occurred during data initialization');
                                console.error(err);
                                this.initFinished();
                            });
                    });
            })
            .catch(error => {
                this.initFailed = true;
            });

        this.schoolClassService.init();
    }

    /**
     * Finished initialization of book viewer.
     * - Set flag to indicate initialization finished
     * - Move to page set on start-up
     */
    private initFinished(): void {
        this.isInitialized = true;
        this.crudService.logDirect(TechnicalFeature.BookOpen);
        let gotoPage;

        /**
         * TODO:
         * This is a workaround which loads the edivdual page to create a session there.
         * This is a temporary workaround, and should be removed as soon as the problem is not available anymore
         * on eduvidual.
         * Start of workaround
         */
        const quickLinks = this.quickLinkService.quickLinks.flatten(this.quickLinkService.quickLinks.genericQuickLinks);
        const edividualRedirect = _.find(quickLinks, (element: QuickLink) => {
            return (element.redirect && element.redirect.toLowerCase().includes('eduvidual.at'));
        });

        if (edividualRedirect) {
            this.edividualSrc = edividualRedirect.redirect;
            this.showEdividualIFrame = true;
            setTimeout(() => {
                this.showEdividualIFrame = false;
            }, 30 * 1000);
        }
        /**
         * End of workaround
         */

        if (environment.debug) {
            const params = new URLSearchParams(window.location.search);
            if (params.get('disableP') === null) {
                gotoPage = +params.get('p');
            }
        }

        if (this.applicationStateService.globalVars && this.applicationStateService.globalVars.startPage) {
            gotoPage = +this.applicationStateService.globalVars.startPage;
        }

        if (gotoPage) {
            const pageChangedEvent = new PageChangedEvent();
            this.positioningService.adjustViewMode($('html')
                .first());
            const index = this.pageService.getIndexFromPageNumber(gotoPage);

            pageChangedEvent.moveToPage = index;
            this.communicationService.announcePageTurn(pageChangedEvent);
        }

        const focusSelectorElement = '.bookviewer-container';
        const intervalId = setInterval(() => {

            if ($(focusSelectorElement).length >= 1) {
                $(focusSelectorElement)
                    .focus();
                clearInterval(intervalId);
            }
        }, 500);
    }

    private calculateDistance($event): number {
        return Math.sqrt(
            Math.pow($event.pointers[0].clientX - $event.pointers[1].clientX, 2)
            + Math.pow($event.pointers[0].clientY - $event.pointers[1].clientY, 2)
        );

    }

    private closeOpenElements() {
        this.communicationService.openDigiFitMenu();
        this.communicationService.openQuickLinkDropdown();
    }

    skipTo(event: Event, id: string): void {
        event.preventDefault();
        this.accessibilityService.focusElementById(id);
    }
}
